/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, TIMER, NORMAL_MODE_MOVE_TYPE } from './constants';
import { checkDtc } from './checkDtc';
import { dispose } from './dispose';
import { eepromClear } from './eepromClear';
import { testMode } from './testMode';
import { sensorCorrection } from './sensorCorrection';
import { normalMode } from './normalMode';

export default {
  name: 'requirement40002',
  data() {
    return {
      phase: PHASE.WAITING,
      isLoading: false,
      systemId: '',
      modal_requirement_40002: false,
      previousPhase: '',
      isTestMode: false,
      elapsedTime: null,
      timer: 0,
      timeoutTimer: null,
      torqueWaitMessage: '',
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR_DTC_RECORDED_IN:
        case PHASE.ERROR_DTC_RECORDED_OUT:
        case PHASE.ERROR_COMMUNICATION:
        case PHASE.ERROR_DTC_OTHER:
          return 'danger';
        case PHASE.COMPLETE:
          return 'success';
        case PHASE.WAITING:
        case PHASE.START:
        case PHASE.INIT_CONFIRM:
        case PHASE.INIT_WAIT:
        case PHASE.INIT_COMPLETE:
        case PHASE.TORQUE_START:
        case PHASE.TORQUE_EXECUTE:
        case PHASE.TORQUE_WAIT:
        case PHASE.STOP:
        case PHASE.END:
        default:
          return 'secondary';
      }
    },

    /** ダイアログのヘッダータイトル */
    headerTitle() {
      // MS2系初期表示、センサ補正初期化確認、センサ補正初期化完了、トルクセンサ補正開始、トルクセンサ補正実行、補正モード終了
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 待機、実行中、トルクセンサ0点補正実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 補正正常終了
      const SuccessTitle = 'message.common_title_success';
      // 通信異常エラー、DTC判定エラー、DTC記録済み（範囲内）エラー、DTC記録済み（範囲外）エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        waiting: ExecutionTitle,
        start: ConfirmationTitle,
        init_confirm: ConfirmationTitle,
        init_wait: ExecutionTitle,
        init_complete: ConfirmationTitle,
        torque_start: ConfirmationTitle,
        torque_execute: ConfirmationTitle,
        torque_wait: ExecutionTitle,
        complete: SuccessTitle,
        stop: ConfirmationTitle,
        error_dtc_recorded_in: ErrorTitle,
        error_dtc_recorded_out: ErrorTitle,
        error_communication: ErrorTitle,
        error_dtc_other: ErrorTitle,
        end: ConfirmationTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        waiting: `${this.i18nWsKey}.REQ_WS_40002_TXT_7`,
        start: `${this.i18nWsKey}.REQ_WS_40002_TXT_1`,
        init_confirm: `${this.i18nWsKey}.REQ_WS_40002_TXT_2`,
        init_wait: `${this.i18nWsKey}.REQ_WS_40002_TXT_7`,
        init_complete: `${this.i18nWsKey}.REQ_WS_40002_TXT_3`,
        torque_start: `${this.i18nWsKey}.REQ_WS_40002_TXT_4`,
        torque_execute: `${this.i18nWsKey}.REQ_WS_40002_TXT_5`,
        torque_wait: this.torqueWaitMessage,
        complete: `${this.i18nWsKey}.REQ_WS_40002_TXT_6`,
        stop: `${this.i18nWsKey}.REQ_WS_40002_TXT_8`,
        error_dtc_recorded_in: `${this.i18nWsKey}.REQ_WS_40002_TXT_9`,
        error_dtc_recorded_out: `${this.i18nWsKey}.REQ_WS_40002_TXT_10`,
        error_communication: `${this.i18nWsKey}.REQ_WS_40002_TXT_11`,
        error_dtc_other: `${this.i18nWsKey}.REQ_WS_40002_TXT_12`,
        end: `${this.i18nWsKey}.REQ_WS_40002_TXT_13`,
      };
      return this.phase === 'torque_wait' ? messageMap[this.phase] : this.$t(messageMap[this.phase]);
    },

    /** 終了ボタン表示判定 */
    showFinishButton() {
      const showFinishButtonMap = {
        waiting: false,
        start: false,
        init_confirm: false,
        init_wait: false,
        init_complete: false,
        torque_start: false,
        torque_execute: false,
        torque_wait: false,
        complete: true,
        stop: false,
        error_dtc_recorded_in: true,
        error_dtc_recorded_out: true,
        error_communication: true,
        error_dtc_other: true,
        end: true,
      };
      return showFinishButtonMap[this.phase];
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_40002 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 現在DTC確認処理
      this.checkDtcProcess();
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_40002 = false;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//
     /** 現在DTC確認処理 */
    async checkDtcProcess() {
      // 現在DTC確認API実行
      await checkDtc(this);
    },

    /** 実行中処理 */
    async initWaitProcess() {
      // 実行中へ遷移
      this.phase = PHASE.INIT_WAIT;
      // センサ補正開始（EEPROM値クリア）API実行
      await eepromClear(this);
    },

    /**
     * センサ補正中止処理
     * @param {string} phase 遷移元画面名
     */
    async stopProcess(phase) {
      // 遷移元画面名を保持して、センサ補正中止へ遷移
      this.previousPhase = phase;
      this.phase = PHASE.STOP;
    },

    /** テストモード移行処理 */
    async testModeProcess() {
      // テストモード移行API実行
      await testMode(this);
    },

    /** トルクセンサ0点補正処理 */
    async sensorCorrectionProcess() {
      // 秒数カウントダウンを初期値設定
      this.torqueWaitMessage = this.$t(`${this.i18nWsKey}.REQ_WS_40002_TXT_14`, [TIMER.MAX_TIMER / 1000]);
      // トルクセンサ0点補正実行中 を表示
      this.phase = PHASE.TORQUE_WAIT;

      // 秒カウントダウン
      this.elapsedTime = setInterval(() => {
        this.timer += TIMER.INTERVAL;
        this.torqueWaitMessage = this.$t(`${this.i18nWsKey}.REQ_WS_40002_TXT_14`, [Math.ceil((TIMER.MAX_TIMER - this.timer)) / 1000]);
        if (TIMER.MAX_TIMER - this.timer <= 0) {
          clearInterval(this.elapsedTime);
        }
      }, TIMER.INTERVAL);

      // 指定時間経過後、トルクセンサ0点補正処理用のポーリング停止APIを実行
      this.timeoutTimer = setTimeout(async () => {
        // カウントダウンタイマー停止
        clearTimeout(this.timeoutTimer);
        this.timeoutTimer = null;
        clearInterval(this.elapsedTime);
        this.timer = 0;
      }, TIMER.MAX_TIMER);

      // トルクセンサ0点補正処理を実行
      await sensorCorrection(this);
    },

    /** [センサ補正中止]OKボタン押下処理 */
    async onStopOkClicked() {
      // 現状モード確認
      if (this.isTestMode) {
        // テストモードの場合、通常モード移行（センサ補正中止）処理を実行
        await normalMode(this, NORMAL_MODE_MOVE_TYPE.STOP);
      } else {
        // 通常モードの場合、補正モード終了へ遷移
        this.phase = PHASE.END;
      }
    },

    /** 戻るボタン押下処理 */
    onBackClicked() {
      switch (this.phase) {
        case PHASE.INIT_CONFIRM:
          // MS2系初期表示へ遷移
          this.phase = PHASE.START;
          break;
        case PHASE.INIT_COMPLETE:
        case PHASE.TORQUE_START:
          // 何もしない（自画面遷移）※顧客希望により（SSM4併せ）
          break;
        default:
          break;
      }
    },

    /** キャンセルボタン押下処理 */
    onCancelClicked() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.INIT_COMPLETE:
        case PHASE.TORQUE_START:
        case PHASE.TORQUE_EXECUTE:
          // センサ補正中止へ遷移
          this.stopProcess(this.phase);
          break;
        case PHASE.INIT_CONFIRM:
          // センサ補正初期化画面のみ、遷移元画面を「MS2系初期表示画面」に設定して、センサ補正中止へ遷移
          this.stopProcess(PHASE.START);
          break;
        case PHASE.STOP:
          // 遷移元画面へ遷移
          this.phase = this.previousPhase;
          break;
        default:
          break;
      }
    },

    /** 次へボタン押下処理 */
    onNextClicked() {
      switch (this.phase) {
        case PHASE.START:
          // センサ補正初期化確認へ遷移
          this.phase = PHASE.INIT_CONFIRM;
          break;
        case PHASE.INIT_CONFIRM:
          // 実行中へ遷移（センサ補正開始（EEPROM値クリア）API実行）
          this.initWaitProcess();
          break;
        case PHASE.INIT_COMPLETE:
          // トルクセンサ補正開始へ遷移
          this.phase = PHASE.TORQUE_START;
          break;
        case PHASE.TORQUE_START:
          // テストモード移行処理
          this.testModeProcess();
          break;
        case PHASE.TORQUE_EXECUTE:
          // トルクセンサ0点補正処理
          this.sensorCorrectionProcess();
          break;
        default:
          break;
      }
    },

    /** 終了ボタン押下 */
    async onFinishClicked() {
      // DisposeAPI実行
      await dispose(this);
      // 結果に関わらず、画面を閉じる
      this.closeWizard();
    },

    //----- 異常コールバックメソッド -----------------------------------------------//
    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR_COMMUNICATION;
    },

    /**
     * API 通信共通バリデーションエラー発生のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
        );
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    // 初期化
    this.init();
  },
};